import clsx from 'clsx';
import * as React from 'react';
import { useSetRecoilState } from 'recoil';

import * as analytics from '@gitbook/app-analytics/client';
import { UserTheme } from '@gitbook/app-database';
import * as hiveCore from '@gitbook/hive-core';
import { useHiveBranch } from '@gitbook/hive-react';
import {
    createPalette,
    PaletteButton,
    PaletteCommandType,
    type PaletteDef,
    useConfigurationTheme,
} from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { Button, ButtonGroup } from '@gitbook/spine-react';
import { useLoadingState, useResizeObserver } from '@gitbook/util-react-hooks';
import { assertIsDefined, type MandateProps } from '@gitbook/util-ts';

import { useImageContentReference, useInsertFiles } from '../../../Components';
import { userThemeAtom } from '../../../state';
import defaultCoverUrl from './assets/default-page-cover.raw.svg';

interface PageCoverProps {
    page: hiveCore.RevisionPageDocument;
    readOnly?: boolean;
    analyticsContext: analytics.AnalyticsContextSpaceContent;
    rounded?: boolean;
}

// Height of the cover in pixels.
const COVER_HEIGHT = 240;

/**
 * Cover for the page when one exists
 */
export function PageCover(props: PageCoverProps): React.ReactElement | null {
    const { page } = props;

    if (!page.cover) {
        return null;
    }

    const pageWithCover = page as MandateProps<hiveCore.RevisionPageDocument, 'cover'>;

    return (
        <React.Suspense fallback={<PageCoverFallback />}>
            <PageVisibleCover {...props} page={pageWithCover} />
        </React.Suspense>
    );
}

/**
 * Fallback when loading the cover.
 */
function PageCoverFallback(): React.ReactElement {
    return (
        <div className="relative w-full" style={{ height: COVER_HEIGHT }}>
            <CoverLoading />
        </div>
    );
}

/**
 * Cover for the page when one exists
 */
function PageVisibleCover(
    props: PageCoverProps & {
        page: MandateProps<hiveCore.RevisionPageDocument, 'cover'>;
    }
): React.ReactElement {
    const { page, readOnly, rounded, analyticsContext } = props;
    const [repositioning, setRepositioning] = React.useState(false);
    const insertFiles = useInsertFiles();
    const hiveBranch = useHiveBranch();
    const [loading, wrapLoading] = useLoadingState();

    // Resolve the cover reference to a URL
    const imageSource = useImageContentReference(page.cover.ref, page.cover.refDark);
    const coverUrl = imageSource?.uri ?? defaultCoverUrl;

    // Position of the cover in image pixels.
    const initialCoverY = page.cover.yPos || 0;
    const [coverY, setCoverY] = React.useState(initialCoverY);

    const handleSaveRepositioning = async () => {
        assertIsDefined(hiveBranch);

        setRepositioning(false);

        await hiveBranch.change((change) => {
            change.movePageCover(page.id, coverY);
        });

        analytics.logEvent({
            ...analyticsContext,
            type: 'edit_reposition_cover',
        });
    };

    const handleCancelRepositioning = () => {
        setRepositioning(false);
        setCoverY(initialCoverY);
    };

    const pickCover = async (coverMode: 'light' | 'dark' | 'all') => {
        assertIsDefined(hiveBranch);

        const [ref] = await insertFiles.prompt({
            title: 'Select cover image',
            accept: 'images',
            acceptURL: true,
        });
        if (ref) {
            await wrapLoading(() =>
                hiveBranch.change((change) => {
                    if (coverMode === 'all') {
                        change.addPageCover(page.id, { ref, refDark: undefined });
                    } else {
                        change.addPageCover(page.id, {
                            [coverMode === 'dark' ? 'refDark' : 'ref']: ref,
                        });
                    }
                })
            );

            analytics.logEvent({
                ...analyticsContext,
                type: 'edit_upload_cover',
                coverMode,
            });
        }
    };

    const removeCover = async () => {
        assertIsDefined(hiveBranch);

        await wrapLoading(() =>
            hiveBranch.change((change) => {
                change.removePageCover(page.id);
            })
        );

        analytics.logEvent({
            ...analyticsContext,
            type: 'edit_remove_cover',
        });
    };

    const updateCoverSize = (coverSize: hiveCore.PageLayoutOptionsCoverSize) => {
        assertIsDefined(hiveBranch);

        hiveBranch.change((change) => {
            change.updatePageLayout(page.id, {
                coverSize,
            });
        });

        analytics.logEvent({
            ...analyticsContext,
            type: 'edit_cover_size',
            coverSize,
        });
    };

    const paletteSource = () =>
        createPalette({
            groups: [
                {
                    key: 'themes',
                    commands: [
                        {
                            key: 'all',
                            icon: Icon.Replace,
                            title: 'Replace image',
                            subtitle: 'For both light and dark modes',
                            trigger: () => {
                                pickCover('all');
                            },
                        },
                        {
                            key: 'light',
                            icon: Icon.ThemeLight,
                            title: 'For light mode',
                            trigger: () => {
                                pickCover('light');
                            },
                        },
                        {
                            key: 'dark',
                            icon: Icon.ThemeDark,
                            title: 'For dark mode',
                            trigger: () => {
                                pickCover('dark');
                            },
                        },
                    ],
                },
            ],
        });

    const palette = () => {
        const currentCoverSize = hiveCore.getPageLayoutOptions(page).coverSize;

        return createPalette({
            groups: [
                {
                    key: 'pageCover',
                    commands: [
                        {
                            key: 'pageCover.select',
                            icon: Icon.Replace,
                            title: 'Replace image',
                            descendant: paletteSource,
                        },
                        {
                            key: 'pageCover.reposition',
                            icon: Icon.ImageSizeBlock,
                            title: 'Reposition',
                            trigger: () => {
                                setRepositioning(true);
                            },
                        },
                    ],
                },
                {
                    key: 'pageCoverSize',
                    commands: [
                        {
                            key: 'pageCoverSize.full',
                            icon: Icon.FullWidth,
                            title: 'Full width',
                            type: PaletteCommandType.CHECKBOX,
                            selected: currentCoverSize === hiveCore.PageLayoutOptionsCoverSize.Full,
                            trigger: () =>
                                updateCoverSize(hiveCore.PageLayoutOptionsCoverSize.Full),
                        },
                        {
                            key: 'pageCoverSize.hero',
                            icon: Icon.DefaultWidth,
                            title: 'Hero width',
                            type: PaletteCommandType.CHECKBOX,
                            selected: currentCoverSize === hiveCore.PageLayoutOptionsCoverSize.Hero,
                            trigger: () =>
                                updateCoverSize(hiveCore.PageLayoutOptionsCoverSize.Hero),
                        },
                    ],
                },
                {
                    key: 'pageCoverDanger',
                    commands: [
                        {
                            key: 'pageCoverDanger.remove',
                            icon: Icon.Trashcan,
                            title: 'Remove cover',
                            trigger: () => removeCover(),
                        },
                    ],
                },
            ],
        });
    };

    const isMultiThemeCover = Boolean(page.cover?.refDark);

    const mainEditButton = page.cover ? (
        <ChangeCoverButton isMultiThemeCover={isMultiThemeCover} palette={palette} />
    ) : null;

    return (
        <div
            className={clsx('group relative w-full', rounded && 'rounded-xl')}
            style={{ height: COVER_HEIGHT }}
        >
            <CoverRepositionableImage
                coverUrl={coverUrl}
                y={coverY}
                onChangeY={setCoverY}
                repositioning={repositioning}
            />
            {loading ? <CoverLoading /> : null}
            {readOnly ? null : (
                <div
                    className={clsx(
                        'absolute right-4 bottom-4 flex gap-2 transition-opacity',
                        repositioning
                            ? 'opacity-100'
                            : 'opacity-0 group-hover:opacity-100 has-[[aria-expanded]]:opacity-100'
                    )}
                >
                    {repositioning ? (
                        <ButtonGroup>
                            <Button
                                label="Cancel"
                                kind="secondary"
                                size="medium"
                                onPress={handleCancelRepositioning}
                            />
                            <Button
                                label="Save"
                                kind="primary"
                                size="medium"
                                onPress={handleSaveRepositioning}
                            />
                        </ButtonGroup>
                    ) : (
                        <ButtonGroup>
                            {isMultiThemeCover ? (
                                <ButtonGroup kind="segmented">
                                    {mainEditButton}
                                    <ThemeToggleButton />
                                </ButtonGroup>
                            ) : (
                                mainEditButton
                            )}
                            <PaletteButton
                                label="Options"
                                iconOnly
                                leadingIcon={Icon.MoreVertical}
                                size="medium"
                                kind="secondary"
                                palette={palette}
                            />
                        </ButtonGroup>
                    )}
                </div>
            )}
        </div>
    );
}

function ChangeCoverButton(props: { isMultiThemeCover: boolean; palette: () => PaletteDef }) {
    const { isMultiThemeCover, palette } = props;
    const theme = useConfigurationTheme();
    return (
        <PaletteButton
            label="Change cover"
            leadingIcon={Icon.Image}
            title={
                isMultiThemeCover
                    ? `Change the cover image for ${theme.color} mode only`
                    : undefined
            }
            kind="secondary"
            size="medium"
            palette={palette}
        />
    );
}

function ThemeToggleButton() {
    const theme = useConfigurationTheme();
    const setUserTheme = useSetRecoilState(userThemeAtom);
    return (
        <Button
            kind="secondary"
            leadingIcon={theme.color === 'light' ? Icon.ThemeDark : Icon.ThemeLight}
            label={`Cover has a ${
                theme.color === 'light' ? 'dark' : 'light'
            } mode alternative.\nToggle theme to see it.`}
            onPress={() => {
                setUserTheme(theme.color === 'light' ? UserTheme.Dark : UserTheme.Light);
            }}
            iconOnly
        />
    );
}

/**
 * Image for the cover with animation and pan responder.
 */
function CoverRepositionableImage(props: {
    coverUrl: string;
    y: number;
    onChangeY: (y: number) => void;
    repositioning: boolean;
}): React.ReactElement | null {
    const { coverUrl, y, onChangeY, repositioning } = props;

    const [imageDimensions, setImageDimensions] = React.useState<{
        width: number;
        height: number;
    } | null>(null);
    const [container, setContainer] = React.useState<{ width: number; height: number } | null>(
        null
    );
    const containerRef = useResizeObserver((entry) => {
        setContainer({ width: entry.contentRect.width, height: entry.contentRect.height });
    });

    const ratio =
        imageDimensions && container
            ? Math.max(
                  container.width / imageDimensions.width,
                  container.height / imageDimensions.height
              )
            : 1;

    const maxOffset =
        imageDimensions && container
            ? (imageDimensions.height * ratio - container.height) / 2 / ratio
            : 0;

    const dragging = React.useRef(false);
    const startY = React.useRef(0);
    const startOffset = React.useRef(0);

    React.useEffect(() => {
        if (!repositioning) {
            return;
        }

        const handleMove = (event: PointerEvent) => {
            if (!dragging.current) {
                return;
            }

            const delta = event.clientY - startY.current;
            const newOffset = startOffset.current + delta / ratio;
            const clamped = Math.max(-maxOffset, Math.min(maxOffset, newOffset));
            onChangeY(clamped);
        };
        const handleUp = () => {
            dragging.current = false;
        };
        window.addEventListener('pointermove', handleMove);
        window.addEventListener('pointerup', handleUp);
        return () => {
            window.removeEventListener('pointermove', handleMove);
            window.removeEventListener('pointerup', handleUp);
        };
    }, [ratio, maxOffset, onChangeY, repositioning]);

    const handlePointerDown = (event: React.PointerEvent) => {
        if (!repositioning) {
            return;
        }

        event.preventDefault();
        dragging.current = true;
        startY.current = event.clientY;
        startOffset.current = y;
    };

    const scaledHeight =
        imageDimensions && container ? imageDimensions.height * ratio : COVER_HEIGHT;
    const top =
        container && imageDimensions ? (container.height - scaledHeight) / 2 + y * ratio : y;

    const ready = !!imageDimensions && !!container;

    return (
        <div
            ref={containerRef}
            className={clsx('absolute inset-0 overflow-hidden', repositioning && 'cursor-grab')}
            {...(ready ? { 'data-testid': 'page.cover' } : {})}
            onPointerDown={handlePointerDown}
        >
            <div
                className={clsx(
                    '-translate-x-1/2 -translate-y-1/2 pointer-events-none absolute top-1/2 left-1/2 rounded-lg bg-black/60 px-4 py-2 text-white opacity-0 transition',
                    'font-medium text-ui-small',
                    repositioning && ready && 'opacity-100'
                )}
            >
                Drag image to reposition
            </div>
            <div
                className={clsx(
                    'absolute inset-0 bg-white/20 backdrop-blur transition-opacity delay-200 duration-500 dark:bg-black/20',
                    ready && 'opacity-0'
                )}
            />
            <img
                src={coverUrl}
                alt="Page cover"
                className="-translate-x-1/2 pointer-events-none absolute left-1/2 object-cover"
                style={{
                    top,
                    width: imageDimensions ? imageDimensions.width * ratio : '100%',
                    height: scaledHeight,
                }}
                onLoad={(event) => {
                    setImageDimensions({
                        width: event.currentTarget.naturalWidth,
                        height: event.currentTarget.naturalHeight,
                    });
                }}
            />
        </div>
    );
}

/**
 * Content of the cover when loading.
 */
function CoverLoading(): React.ReactElement {
    return <div className="shimmer absolute inset-0 flex items-center justify-center bg-muted" />;
}
